import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import {
  buildFileTree,
  flattenVisible,
  isFolderOpen,
  languageOf,
  type TreeNode,
} from "@/lib/packFileTree";
import { layerBadge, type PackFileLike } from "@/lib/packFiles";
import { cn } from "@/lib/cn";

const INDENT_PX = 12;

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className={cn(
        "size-3 shrink-0 transition-transform",
        open && "rotate-90",
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M6 3l5 5-5 5" />
    </svg>
  );
}

function FolderIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-3.5 shrink-0 text-muted-foreground"
      fill={open ? "none" : "currentColor"}
      fillOpacity={open ? 0 : 0.25}
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <path d="M1.5 3.5h4l1.5 1.5h7.5v8h-13z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-3.5 shrink-0 text-muted-foreground"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <path d="M3.5 1.5h6l3 3v10h-9zM9.5 1.5v3h3" />
    </svg>
  );
}

function Highlighted({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  const at = needle === "" ? -1 : text.toLowerCase().indexOf(needle);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-sm bg-accent px-0 text-accent-foreground">
        {text.slice(at, at + needle.length)}
      </mark>
      {text.slice(at + needle.length)}
    </>
  );
}

/**
 * Compact file tree. Folders are collapsed except the path to the selected
 * file; a search opens every matching branch. Roving tabindex, arrow keys.
 */
export function FileTree<T extends PackFileLike>({
  files,
  query,
  selected,
  onSelect,
  tenantName = "tenant",
  noteOf,
}: {
  files: readonly T[];
  /** Names the tenant pack in the per-file badge ("sheba", "sheba +"). */
  tenantName?: string;
  query: string;
  selected: string | null;
  onSelect: (path: string) => void;
  /** A small muted line under an edited file's name ("Edited by Dana · 3 h ago"). */
  noteOf?: (file: T) => string | null;
}) {
  const tree = useMemo(() => buildFileTree(files), [files]);
  const searching = query.trim() !== "";
  // Manual open/close choices, dropped when the search text changes.
  const [manual, setManual] = useState<{
    query: string;
    map: ReadonlyMap<string, boolean>;
  }>({ query, map: new Map() });
  const overrides =
    manual.query === query ? manual.map : new Map<string, boolean>();
  const [focusId, setFocusId] = useState<string | null>(null);
  const moveFocus = useRef(false);
  const rootRef = useRef<HTMLUListElement>(null);

  const isOpen = (id: string) =>
    isFolderOpen(id, { searching, selected, overrides });
  const rows = flattenVisible(tree, isOpen);
  const activeId =
    rows.find((row) => row.node.id === focusId)?.node.id ??
    rows.find((row) => row.node.id === selected)?.node.id ??
    rows[0]?.node.id ??
    null;

  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    rootRef.current
      ?.querySelectorAll<HTMLElement>('[role="treeitem"]')
      .forEach((el) => {
        if (el.dataset.nodeId === activeId) el.focus();
      });
  }, [activeId]);

  const toggle = (id: string, open: boolean) =>
    setManual({ query, map: new Map(overrides).set(id, open) });

  const focusNode = (id: string | undefined) => {
    if (id === undefined) return;
    moveFocus.current = true;
    setFocusId(id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const index = rows.findIndex((row) => row.node.id === activeId);
    const row = rows[index];
    if (!row) return;
    const { node } = row;
    switch (event.key) {
      case "ArrowDown":
        focusNode(rows[index + 1]?.node.id);
        break;
      case "ArrowUp":
        focusNode(rows[index - 1]?.node.id);
        break;
      case "Home":
        focusNode(rows[0]?.node.id);
        break;
      case "End":
        focusNode(rows[rows.length - 1]?.node.id);
        break;
      case "ArrowRight":
        if (node.kind !== "folder") return;
        if (!row.open) toggle(node.id, true);
        else focusNode(rows[index + 1]?.node.id);
        break;
      case "ArrowLeft":
        if (node.kind === "folder" && row.open) toggle(node.id, false);
        else if (row.parentId !== null) focusNode(row.parentId);
        break;
      case "Enter":
      case " ":
        if (node.kind === "folder") toggle(node.id, !row.open);
        else onSelect(node.id);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const renderNodes = (nodes: readonly TreeNode<T>[], level: number) =>
    nodes.map((node) => {
      const open = node.kind === "folder" && isOpen(node.id);
      const edited = node.kind === "file" ? node.file.edited : node.edited;
      const lang = node.kind === "folder" ? languageOf(node.name) : null;
      const isSelected = node.kind === "file" && node.id === selected;
      const note = node.kind === "file" ? (noteOf?.(node.file) ?? null) : null;
      const title = node.kind === "file" ? node.id : `${node.id}/`;
      const badge =
        node.kind === "file" ? layerBadge(node.file.layer, tenantName) : null;
      return (
        // Keyboard handling lives on the tree (roving tabindex).
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <li
          key={`${node.kind}:${node.id}`}
          role="treeitem"
          data-node-id={node.id}
          aria-level={level}
          aria-label={`${node.name}${badge ? `, ${badge.title}` : ""}${edited ? ", edited" : ""}`}
          aria-expanded={node.kind === "folder" ? open : undefined}
          aria-selected={node.kind === "file" ? isSelected : undefined}
          tabIndex={node.id === activeId ? 0 : -1}
          onClick={(event) => {
            event.stopPropagation();
            setFocusId(node.id);
            if (node.kind === "folder") toggle(node.id, !open);
            else onSelect(node.id);
          }}
          onFocus={(event) => {
            if (event.target === event.currentTarget) setFocusId(node.id);
          }}
          className="list-none outline-none focus-visible:[&>div]:ring-2 focus-visible:[&>div]:ring-ring"
        >
          <div
            title={title}

            style={{ paddingLeft: 4 + (level - 1) * INDENT_PX }}
            className={cn(
              "flex min-h-6 cursor-pointer items-center gap-1 rounded-sm pr-1 text-sm hover:bg-secondary",
              isSelected && "bg-secondary font-medium",
              lang && "font-semibold text-foreground",
            )}
          >
            {node.kind === "folder" ? (
              <>
                <Chevron open={open} />
                <FolderIcon open={open} />
              </>
            ) : (
              <>
                <span className="size-3 shrink-0" />
                <FileIcon />
              </>
            )}
            <span className="min-w-0 flex-1">
              <span dir="ltr" className="block truncate">
                <Highlighted text={node.name} query={query} />
              </span>
              {note ? (
                <span className="block truncate text-[10px] font-normal text-muted-foreground">
                  {note}
                </span>
              ) : null}
            </span>
            {lang ? (
              <span className="shrink-0 rounded bg-accent px-1 text-[10px] font-semibold uppercase text-accent-foreground">
                {lang}
              </span>
            ) : null}
            {badge ? (
              <span
                aria-hidden="true"
                title={badge.title}
                className="shrink-0 rounded border border-border px-1 text-[10px] font-medium text-muted-foreground"
              >
                {badge.label}
              </span>
            ) : null}
            {edited ? (
              <span
                aria-hidden="true"
                title="Edited"
                className="size-2 shrink-0 rounded-full bg-primary"
              />
            ) : null}
          </div>
          {node.kind === "folder" && open ? (
            <ul role="group" className="m-0 p-0">
              {renderNodes(node.children, level + 1)}
            </ul>
          ) : null}
        </li>
      );
    });

  return (
    <ul
      ref={rootRef}
      role="tree"
      aria-label="Pack files"
      onKeyDown={onKeyDown}
      className="m-0 max-h-[calc(100vh-18rem)] min-h-40 overflow-y-auto p-0"
    >
      {renderNodes(tree, 1)}
    </ul>
  );
}
